import Modal from "@/components/design-system/Modal";
import { Slider } from "@/components/ui/slider";
import { errorToast } from "@/utils/constants";
import { ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { getCroppedImageDataUrl } from "../utils/cropImage";

const CROP_MIN_HEIGHT = 400;
const CROP_TARGET_HEIGHT = 480;
/** Header + zoom controls + footer + modal padding (~288px) */
const CROP_CHROME_OFFSET = "18rem";
const ASPECT = 16 / 9;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const ZOOM_ICON =
  "shrink-0 text-[#6B7280] transition-opacity hover:opacity-70";

const ZOOM_SLIDER =
  "flex-1 [&>span:first-child]:h-[6px] [&>span:first-child]:rounded-full [&>span:first-child]:bg-[#E2E8F0] [&>span:first-child>span]:rounded-full [&>span:first-child>span]:bg-[#5C88C5] [&_[role=slider]]:!h-5 [&_[role=slider]]:!w-5 [&_[role=slider]]:!border-2 [&_[role=slider]]:!border-[#5C88C5] [&_[role=slider]]:!bg-white [&_[role=slider]]:shadow-none [&_[role=slider]]:focus-visible:ring-0";

interface ThumbnailCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  imageLoading?: boolean;
  onClose: () => void;
  onSave: (croppedDataUrl: string) => void | Promise<void>;
}

export default function ThumbnailCropModal({
  isOpen,
  imageSrc,
  imageLoading = false,
  onClose,
  onSave,
}: ThumbnailCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, [isOpen, imageSrc]);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta)));
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const dataUrl = await getCroppedImageDataUrl(imageSrc, croppedAreaPixels);
      await onSave(dataUrl);
    } catch {
      errorToast(
        "Crop failed",
        "Image could not be loaded for cropping. Please retry or upload the image again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Crop Image"
      subtitle="Resize the image to match the frame for optimal display. Recommended aspect ratio for thumbnail is 16:9"
      width="100%"
      maxWidth="896px"
      fitViewport
      className="mx-4"
      contentClassName="p-0"
      titleClassName="text-[20px] font-semibold leading-[28px] text-[#1F1F1F]"
      subtitleClassName="mt-1 text-[14px] font-normal leading-[20px] text-[#7B7B7B]"
    >
      <div
        className="relative w-full shrink-0 overflow-hidden bg-[#1F1F1F]"
        style={{
          height: `clamp(${CROP_MIN_HEIGHT}px, calc(100dvh - ${CROP_CHROME_OFFSET}), ${CROP_TARGET_HEIGHT}px)`,
        }}
        onWheel={handleWheel}
      >
        {imageLoading || !imageSrc ? (
          <div className="flex h-full items-center justify-center text-[14px] font-medium text-white">
            Loading image…
          </div>
        ) : (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="horizontal-cover"
          />
        )}
      </div>

      <div className="shrink-0 px-6 pt-4 pb-2">
        <div className="mx-auto flex w-full max-w-[475px] items-center gap-3">
          <button
            type="button"
            className={ZOOM_ICON}
            aria-label="Zoom out"
            onClick={() =>
              setZoom((z) => Math.max(MIN_ZOOM, Number((z - 0.1).toFixed(2))))
            }
          >
            <ZoomOut className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <Slider
            value={[zoom]}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            onValueChange={([v]) => setZoom(v)}
            className={ZOOM_SLIDER}
          />
          <button
            type="button"
            className={ZOOM_ICON}
            aria-label="Zoom in"
            onClick={() =>
              setZoom((z) => Math.min(MAX_ZOOM, Number((z + 0.1).toFixed(2))))
            }
          >
            <ZoomIn className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>
        <p className="mt-3 text-center text-[12px] font-normal leading-[16px] text-[#6B7280]">
          Drag to move • Scroll or use slider to zoom
        </p>
      </div>

      <div className="flex shrink-0 justify-end gap-[20px] border-t border-[#E5E7EB] px-6 py-4">
        <button
          type="button"
          className="h-[48px] max-h-[48px] w-[120px] rounded-[12px] border border-[#5C88C5] bg-white text-[18px] font-semibold text-[#1F1F1F] transition-colors hover:bg-[#F5FBFF] disabled:opacity-50"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="h-[48px] max-h-[48px] w-[142px] rounded-[12px] bg-[#5C88C5] px-[12px] py-[13px] text-[18px] font-semibold text-white transition-colors hover:bg-[#4A73A8] disabled:opacity-50"
          onClick={handleSave}
          disabled={saving || !croppedAreaPixels || imageLoading || !imageSrc}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}
